import { Link, Breadcrumbs as MuiBreadcrumbs, Skeleton, SxProps } from '@mui/material';
import { t } from 'i18next';
import { memo } from 'react';
import { Breadcrumb } from '../../breadcrumbs/models/Breadcrumb';
import { TypographyOverflow } from '../../common/components/TypographyOverflow';
import { DocumentTitle } from './DocumentTitle';

type Props = {
  getBreadcrumbs: () => Breadcrumb[];
  loading: boolean;
};

export const Breadcrumbs = memo(({ getBreadcrumbs, loading }: Props) => {
  const breadcrumbs = getBreadcrumbs();

  if (breadcrumbs.length === 0) {
    return null;
  }

  // Compute the indexes of the breadcrumbs that should shrink on small screens:
  // the `nth-of-type` selector will also match the separators and its indexes start
  // from 1. Return the index considering the separator offset:
  // - first breadcrumb is shrinkable -> index = 1
  // - nth breadcrumb is shrinkable -> there will be n - 1 separators to account for -> 2n + 1
  const shrinkableIndexes = breadcrumbs.map((b, index) => (b.shrink ? 2 * index + 1 : null)).filter((i) => i !== null);
  const shrinkableSelector = shrinkableIndexes.map((i) => `& .MuiBreadcrumbs-li:nth-of-type(${i})`).join(',');
  const shrinkableSx: SxProps = {
    '.MuiBreadcrumbs-ol': {
      flexWrap: 'nowrap',
      lineHeight: 0,
      [shrinkableSelector]: {
        display: 'inline-flex',
        flexGrow: 0,
        flexShrink: 1,
        overflow: 'hidden',
        a: { width: 1 },
      },
    },
  };

  const breadcrumbsSx = !loading && shrinkableIndexes.length > 0 ? shrinkableSx : {};

  return (
    <>
      <DocumentTitle loading={loading} fragments={breadcrumbs.map((b) => b.name)} />
      <MuiBreadcrumbs aria-label={t('a11y.navigation.breadcrumbs') as string} sx={breadcrumbsSx}>
        {breadcrumbs.map((crumb, index) => {
          if (loading) {
            return <Skeleton key={index} width={100} variant="text" />;
          }
          return (
            <Link sx={{ border: 'none', '&:hover, &:focus': { color: 'primary.main' } }} variant="body2" key={index} color="inherit" href={crumb.path}>
              <TypographyOverflow>{crumb.name}</TypographyOverflow>
            </Link>
          );
        })}
      </MuiBreadcrumbs>
    </>
  );
});
